/**
 * A caixa de escrever do Assistente: o campo cresce com o texto, Enter
 * pergunta e Shift+Enter quebra a linha. Respondendo a uma resposta, a
 * citação aparece em cima (ESC no campo tira a citação). Embaixo, quantas
 * perguntas sobram hoje.
 */
import React, { useLayoutEffect } from "react";
import { FiArrowUp } from "react-icons/fi";
import styled from "styled-components";

import { FaixaDaCitacao } from "../conversas/CaixaDeEnvio";
import { CELULAR } from "../conversas/estilos";
import { Coluna, mensagemDaCitacao } from "./Turnos";

export const TAMANHO_MAXIMO_DA_PERGUNTA = 800;

const Formulario = styled.form`
  flex: 0 0 auto;
  padding: 10px 16px calc(10px + var(--safe-area-bottom, 0px));
  border-top: 1px solid var(--border);
  background: var(--surface);
`;

const CitacaoNaEscrita = styled.div`
  margin-bottom: 8px;
`;

const Caixa = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 6px 6px 14px;
  border-radius: 14px;
  border: 1px solid var(--border-strong);
  background: var(--surface);

  &:focus-within {
    border-color: var(--accent);
    box-shadow: var(--shadow-focus);
  }

  textarea {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 22px;
    max-height: 168px;
    padding: 6px 0;
    border: 0;
    outline: none;
    resize: none;
    background: transparent;
    color: var(--ink);
    font: inherit;
    font-size: 14px;
    line-height: 1.5;

    &::placeholder {
      color: var(--ink-3);
    }
  }
`;

const Enviar = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: var(--accent);
  color: var(--ink-on-accent);
  cursor: pointer;
  transition: opacity 120ms ease-out, transform 120ms ease-out;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Dica = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  padding: 0 4px;
  font-size: 12px;
  color: var(--ink-3);

  @media ${CELULAR} {
    > span:first-child {
      display: none;
    }
  }
`;

const Contador = styled.span`
  ${({ $acabando }) => ($acabando ? "color: var(--warning-text); font-weight: 600;" : "")}
`;

function useCampoQueCresce(campo, valor) {
  useLayoutEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [campo, valor]);
}

/**
 * `campo`: ref do textarea (a página foca depois de responder e de citar).
 * `contador`: `{texto, acabando}` ou null.
 */
export default function Escrita({ campo, pergunta, aoMudar, aoPerguntar, enviando, citando, aoCancelarCitacao, contador }) {
  useCampoQueCresce(campo, pergunta);
  return (
    <Formulario
      onSubmit={(e) => {
        e.preventDefault();
        aoPerguntar();
      }}
    >
      <Coluna>
        {citando ? (
          <CitacaoNaEscrita>
            <FaixaDaCitacao mensagem={mensagemDaCitacao(citando)} titulo="Respondendo ao assistente" aoCancelar={aoCancelarCitacao} />
          </CitacaoNaEscrita>
        ) : null}
        <Caixa>
          <textarea
            ref={campo}
            rows={1}
            value={pergunta}
            maxLength={TAMANHO_MAXIMO_DA_PERGUNTA}
            aria-label="Pergunta ao assistente"
            placeholder="Pergunte sobre um cliente, prazo ou pendência"
            onChange={(e) => aoMudar(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && citando) {
                e.preventDefault();
                aoCancelarCitacao();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                aoPerguntar();
              }
            }}
          />
          <Enviar type="submit" disabled={enviando || !pergunta.trim()} aria-label="Perguntar" title="Perguntar (Enter)">
            <FiArrowUp size={18} aria-hidden="true" />
          </Enviar>
        </Caixa>
        <Dica>
          <span>Enter pergunta · Shift+Enter quebra a linha</span>
          {contador ? <Contador $acabando={contador.acabando}>{contador.texto}</Contador> : null}
        </Dica>
      </Coluna>
    </Formulario>
  );
}
